import { useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router';
import { useAuth } from '../contexts/AuthContext';
import { Routes } from '../types/enums/Routes.enum';

export interface Permission {
    modulo: string;
    permiso?: string;
}

export interface AuthorizationOptions {
    redirectTo?: string;
    requireAll?: boolean;
    onUnauthorized?: () => void;
    fallbackComponent?: React.ReactNode;
}

export interface AuthorizationResult {
    isAuthorized: boolean;
    isLoading: boolean;
    missingPermissions: Permission[];
    user: any;
}

/**
 * Hook avanzado para manejar autorización y permisos de manera elegante
 */
export const useAuthorization = (
    requiredPermissions: Permission[] = [],
    options: AuthorizationOptions = {}
) => {
    const auth = useAuth();
    const navigate = useNavigate();
    const location = useLocation();

    const {
        redirectTo = Routes.DEFAULT,
        requireAll = true,
        onUnauthorized,
        fallbackComponent
    } = options;

    /**
     * Verificar si el usuario está completamente autenticado
     */
    const isAuthenticated = useMemo(() => {
        return auth.state.isAuthenticated && !auth.isTokenExpired();
    }, [auth.state.isAuthenticated, auth.isTokenExpired]);

    /**
     * Calcular permisos faltantes
     */
    const missingPermissions = useMemo(() => {
        if (!isAuthenticated || !requiredPermissions.length) {
            return [];
        }

        return requiredPermissions.filter(({ modulo, permiso }) => {
            return !auth.hasPermission(modulo, permiso);
        });
    }, [isAuthenticated, requiredPermissions, auth]);

    /**
     * Determinar si el usuario está autorizado
     */
    const isAuthorized = useMemo(() => {
        if (!isAuthenticated) return false;
        if (!requiredPermissions.length) return true;

        if (requireAll) {
            return missingPermissions.length === 0;
        } else {
            // Requiere al menos uno
            return missingPermissions.length < requiredPermissions.length;
        }
    }, [isAuthenticated, requiredPermissions.length, missingPermissions.length, requireAll]);

    /**
     * Resultado completo de la autorización
     */
    const authorizationResult: AuthorizationResult = useMemo(() => ({
        isAuthorized,
        isLoading: auth.state.isLoading,
        missingPermissions,
        user: auth.state.user
    }), [isAuthorized, auth.state.isLoading, missingPermissions, auth.state.user]);

    /**
     * Función para requerir autenticación
     */
    const requireAuth = useCallback(() => {
        if (!isAuthenticated) {
            const currentPath = location.pathname + location.search;
            const from = currentPath !== Routes.LOGIN ? currentPath : Routes.DEFAULT;
            
            navigate(Routes.LOGIN, { 
                replace: true, 
                state: { from } 
            });
            return false;
        }
        return true;
    }, [isAuthenticated, navigate, location]);

    /**
     * Función para requerir autorización completa
     */
    const requireAuthorization = useCallback(() => {
        // Primero verificar autenticación
        if (!requireAuth()) {
            return false;
        }

        // Luego verificar autorización
        if (!isAuthorized) {
            if (onUnauthorized) {
                onUnauthorized();
            } else {
                navigate(redirectTo, {
                    replace: true,
                    state: {
                        message: 'No tienes permisos suficientes para acceder a esta sección.',
                        severity: 'warning',
                        from: location.pathname,
                        missingPermissions
                    }
                });
            }
            return false;
        }

        return true;
    }, [requireAuth, isAuthorized, onUnauthorized, navigate, redirectTo, location.pathname, missingPermissions]);

    /**
     * Verificar un permiso específico
     */
    const checkPermission = useCallback((modulo: string, permiso?: string): boolean => {
        if (!isAuthenticated) return false;
        return auth.hasPermission(modulo, permiso);
    }, [isAuthenticated, auth]);

    /**
     * Verificar múltiples permisos con diferentes estrategias
     */
    const checkPermissions = useCallback((
        permissions: Permission[], 
        strategy: 'all' | 'any' = 'all'
    ): boolean => {
        if (!isAuthenticated) return false;

        if (strategy === 'any') {
            return permissions.some(({ modulo, permiso }) => 
                auth.hasPermission(modulo, permiso)
            );
        } else {
            return permissions.every(({ modulo, permiso }) => 
                auth.hasPermission(modulo, permiso)
            );
        }
    }, [isAuthenticated, auth]);

    /**
     * Obtener permisos del usuario para un módulo específico
     */
    const getModulePermissions = useCallback((moduleName: string) => {
        if (!isAuthenticated || !auth.state.user?.permisos) {
            return [];
        }

        return auth.state.user.permisos.filter(p => 
            p.Modulo.toLowerCase() === moduleName.toLowerCase() && p.Activo
        );
    }, [isAuthenticated, auth.state.user?.permisos]);

    /**
     * Obtener todos los módulos disponibles para el usuario
     */
    const getAvailableModules = useCallback(() => {
        if (!isAuthenticated || !auth.state.user?.permisos) {
            return [];
        }

        const modules = new Set<string>();
        auth.state.user.permisos.forEach(p => {
            if (p.Activo) {
                modules.add(p.Modulo);
            }
        });

        return Array.from(modules);
    }, [isAuthenticated, auth.state.user?.permisos]);

    /**
     * Función para logout con callback personalizado
     */
    const logout = useCallback(async (redirectPath?: string) => {
        try {
            await auth.logout();
            navigate(redirectPath || Routes.LOGIN, { replace: true });
        } catch (error) {
            console.error('Error durante logout:', error);
            navigate(Routes.LOGIN, { replace: true });
        }
    }, [auth, navigate]);

    /**
     * Verificar si el token está próximo a expirar (5 minutos)
     */
    const isTokenNearExpiry = useCallback(() => {
        if (!auth.state.expiresAt) return false;
        
        try {
            const expirationTime = new Date(auth.state.expiresAt).getTime();
            const currentTime = new Date().getTime();
            const fiveMinutes = 5 * 60 * 1000;
            
            return (expirationTime - currentTime) <= fiveMinutes;
        } catch {
            return true;
        }
    }, [auth.state.expiresAt]);

    /**
     * Refrescar token si es necesario
     */
    const ensureValidToken = useCallback(async () => {
        if (isTokenNearExpiry()) {
            try {
                await auth.refreshToken();
                return true;
            } catch (error) {
                console.error('Error refrescando token:', error);
                await logout();
                return false;
            }
        }
        return true;
    }, [isTokenNearExpiry, auth, logout]);

    return {
        // Estado
        ...authorizationResult,
        isAuthenticated,
        isTokenExpired: auth.isTokenExpired,
        isTokenNearExpiry: isTokenNearExpiry(),
        
        // Acciones principales
        requireAuth,
        requireAuthorization,
        logout,
        ensureValidToken,
        
        // Verificaciones de permisos
        checkPermission,
        checkPermissions,
        hasPermission: auth.hasPermission,
        
        // Utilidades de permisos
        getModulePermissions,
        getAvailableModules,
        
        // Configuración
        requiredPermissions,
        options
    };
};

/**
 * Hook simplificado para casos básicos
 */
export const useSimpleAuth = () => {
    const auth = useAuth();
    const navigate = useNavigate();

    const isValidSession = useMemo(() => {
        return auth.state.isAuthenticated && !auth.isTokenExpired();
    }, [auth.state.isAuthenticated, auth.isTokenExpired]);

    const redirectToLogin = useCallback(() => {
        navigate(Routes.LOGIN, { replace: true });
    }, [navigate]);

    return {
        isAuthenticated: isValidSession,
        isLoading: auth.state.isLoading,
        user: auth.state.user,
        login: auth.login,
        logout: async () => {
            await auth.logout();
            redirectToLogin();
        },
        requireAuth: () => {
            if (!isValidSession) {
                redirectToLogin();
                return false;
            }
            return true;
        }
    };
};
